/**
 * Capability explainers — the SC-UX03 presentation layer for 所需能力.
 *
 * WHAT THIS IS
 *   An EXPLICIT, REVIEWED binding from (issue code, source capability code) to one editorial
 *   explainer topic, plus the topic's Chinese explanation and the technical examples that
 *   topic may name. The binding is a lookup table written out in this file — never an array
 *   index, a name match, a keyword search or a global "all products" list. An issue that
 *   binds nothing gets nothing; it does not fall back to every device in the scenario.
 *
 * WHAT IT IS NOT
 *   Not a domain contract, not a capability definition, not a local asset record, and not a
 *   claim that anything is available, matched, verified or dispatchable. `issueRelations` and
 *   `entryStudies` in `scenario-research-reference.ts` remain the authority for WHICH source
 *   capability an issue links to; this file only says how to explain that capability to a
 *   reader and which official pages are relevant. Removing a row here cannot add or delete a
 *   source capability.
 *
 * WHY THE BINDINGS ARE NOT DERIVED FROM THE KIT'S ALIASES
 *   The UX03 kit's `aliasesForReviewOnly` lists are for a human reviewer. Matching on them in
 *   code is exactly the name-similarity mapping the repository forbids. Every row below was
 *   written by comparing the source capability's own description with the topic's definition
 *   and recording the reason in `mapping-review.json`.
 *
 * MEDIA RIGHTS
 *   SC-UX04 uses the user-supplied original WebP illustrations in scenario-media.ts.
 *   Third-party product images and videos remain link-only. Two incomplete topic families
 *   remain pending professional Owner review and are isolated by bindingPendingOwner.
 */

export type CapabilityTopicKey =
  | 'ux04-forest-monitoring'
  | 'ux03-aerial-observation'
  | 'ux03-indoor-mapping'
  | 'ux03-ventilation'
  | 'ux03-communications'
  | 'ux03-gas-detection'
  | 'ux03-movement-monitoring'
  | 'ux03-remote-suppression'
  | 'ux03-person-search'
  | 'ux03-assisted-transfer'
  | 'ux03-building-information';

export interface CapabilityTopic {
  readonly key: CapabilityTopicKey;
  readonly nameZh: string;
  /** One sentence: what this capability does. */
  readonly definitionZh: string;
  /** What a reader gets out of it. Two or three short items. */
  readonly outputsZh: readonly string[];
  /** How it can be implemented. Deliberately not a product list. */
  readonly implementationWaysZh: readonly string[];
  /** What it does not do. At most three, and each one earns its place. */
  readonly limitationsZh: readonly string[];
  /** The direct technical examples. Empty means none passed review for this topic. */
  readonly exampleIds: readonly string[];
  /**
   * The purpose in a specific issue, keyed by source issue code.
   *
   * A topic may serve several issues; what changes is why it matters for THIS one. A missing
   * key is not filled with another issue's sentence — the drawer falls back to the repository's
   * own per-issue editorial text instead.
   */
  readonly purposes: Readonly<Record<string, string>>;
}

export const capabilityTopics: readonly CapabilityTopic[] = [
  {
    key: 'ux03-aerial-observation',
    nameZh: '高处观察与热成像',
    definitionZh: '获取建筑外围的可见光和热成像信息，辅助整理可观察区域的线索。',
    outputsZh: ['外部图像或视频', '热分布画面', '带时间和位置的观察记录'],
    implementationWaysZh: ['合适的无人机与相机配置', '其他已核查的远程观察方式'],
    limitationsZh: ['遮挡区域可能观察不到；热像不是穿墙观察。', '热像画面不等于气体成分检测。'],
    exampleIds: ['EX-DJI-M30T'],
    purposes: {
      'HRF-HZ-02': '从建筑外围观察可见的烟气位置和受影响的外部区域，补充室内信息不足时的观察线索。',
      'HRF-HZ-01': '从建筑外围获取可见光和热成像画面，整理可观察部位的火情分布及变化。',
      'HRF-HZ-06': '观察高处外墙、外装饰和构件的可见异常，帮助标记需要专业核查的位置。',
      'HRF-HZ-05': '在危险高处保持距离获取外围损伤线索，为专业评估补充可观察信息，不替代结构评估。',
    },
  },
  {
    key: 'ux03-indoor-mapping',
    nameZh: '室内空间检查',
    definitionZh: '获取室内可观察空间的图像和几何信息，帮助把检查发现对应到位置。',
    outputsZh: ['检查图像', '可用配置下的三维空间结果', '按位置归档的检查记录'],
    implementationWaysZh: ['专业人员与资料核对', '具备相应空间获取功能的室内检查设备'],
    limitationsZh: ['工业检查能力不等于高温浓烟火场适用。', '空间结果不自动证明通道安全。'],
    exampleIds: ['EX-ELIOS-3'],
    purposes: {
      'HRF-HZ-02':
        '受烟气影响的楼梯间或竖井可能缺少可近距离核实的空间线索，室内检查可补充位置信息，但不据此认定路线安全。',
      'HRF-HZ-03': '了解内部空间和相关位置关系，为整理人员线索及需要核查的区域提供空间背景。',
      'HRF-HZ-05': '获取可观察区域的空间形态，辅助把检查记录对应到具体位置。',
      'HRF-HZ-04': '整理通道布局和可见障碍，帮助专业人员确定仍需核实的通行条件。',
    },
  },
  {
    key: 'ux03-ventilation',
    nameZh: '烟气控制与通风支持',
    definitionZh: '了解烟气控制相关的物理支持及其实现方式，结合建筑系统与专业判断使用。',
    outputsZh: ['风机或建筑系统产生的气流作用', '需由另行观察记录的烟气变化'],
    implementationWaysZh: ['建筑既有相关系统', '便携风机', '装有对应通风模块的设备'],
    limitationsZh: ['本页不提供现场布置、开机时机或运行参数。', '设备配置与建筑条件须实际核实。'],
    exampleIds: ['EX-BATFAN', 'EX-COLOSSUS-FAN'],
    purposes: {
      'HRF-HZ-02':
        '在专业人员核实建筑系统与通风条件后，风机或既有系统可对气流产生物理作用；区分观察、检测和通风这三类不同的物理支持。',
    },
  },
  {
    key: 'ux03-communications',
    nameZh: '现场通信与信息回传',
    definitionZh: '支持现场不同位置之间交换语音、图像和位置信息。',
    outputsZh: ['语音或数据传递', '观察画面回传', '带位置的现场记录'],
    implementationWaysZh: ['人员通信终端', '适用的临时组网与中继方案'],
    limitationsZh: [
      '覆盖、频段、供电和链路状态必须在具体环境验证。',
      '产品方案视频不代表本平台已经组网。',
    ],
    exampleIds: ['EX-HYTERA'],
    purposes: {
      'HRF-HZ-02': '传递烟气观察的位置、时间和人员报告，使不同位置的信息可以对照。',
      'HRF-HZ-03': '支持人员线索、联络信息和协助需求的传递，减少信息分散。',
      'HRF-HZ-01': '回传观察图像与变化信息，使后续判断使用同一时点的背景。',
      'HRF-HZ-06': '传递高处异常观察和周边关注信息，保持相关人员知情。',
      'HRF-HZ-04': '传递路径和障碍位置，支持对通行信息的复核。',
      'HRF-HZ-05': '传递损伤观察与位置记录，使不同位置的线索保持同一时点背景。',
    },
  },
  {
    key: 'ux03-gas-detection',
    nameZh: '环境气体检测',
    definitionZh: '使用具备相应传感器的设备提供环境气体测量，帮助补充判断所需的信息。',
    outputsZh: ['相应传感器读数', '报警与测量记录'],
    implementationWaysZh: ['经核验的手持检测仪', '其他具备对应传感器与采样条件的设备'],
    limitationsZh: [
      '可检测对象和量程依实际传感器配置。',
      '读数是否适用取决于采样方式、设备和现场条件。',
    ],
    exampleIds: ['EX-XAM8000'],
    purposes: {
      'HRF-HZ-02': '按实际传感器检测有关气体，补足肉眼和热像无法提供的环境测量信息。',
    },
  },
  {
    key: 'ux03-movement-monitoring',
    nameZh: '结构位移监测',
    definitionZh: '跟踪选定目标的位移变化，向专业人员提供监测信息。',
    outputsZh: ['目标变化记录', '相应的告警提示'],
    implementationWaysZh: ['适用的激光监测设备', '专业测量与现场评估'],
    limitationsZh: [
      '测量目标的位置变化不是建筑整体安全鉴定。',
      '设备观测条件与目标适用性需要核验。',
    ],
    exampleIds: ['EX-SENTRY'],
    purposes: {
      'HRF-HZ-05': '记录选定目标的位置变化，为专业人员评估结构异常提供监测线索。',
      'HRF-HZ-06': '对适合测量的具体目标持续记录变化，作为专业核查的一部分。',
    },
  },
  {
    key: 'ux03-remote-suppression',
    nameZh: '远程灭火支持',
    definitionZh: '了解通过远程控制设备提供灭火物理支持的实现方式。',
    outputsZh: ['相应模块的远程受控水流等物理动作', '设备状态和作业观察记录'],
    implementationWaysZh: ['具备相应模块的消防机器人', '适用的远程控制设施'],
    limitationsZh: [
      '供水、通行、承载、运输及操作授权均需确认。',
      '不推断可以进入任意楼层或任意火场。',
    ],
    exampleIds: ['EX-COLOSSUS-WATER'],
    purposes: {
      'HRF-HZ-01': '了解在设备、路径、供水和授权条件具备时，远程设备可提供的灭火物理支持。',
    },
  },
  {
    key: 'ux03-person-search',
    nameZh: '人员线索搜寻',
    definitionZh: '组织与人员有关的观察和联络线索，并保留需要进一步核实的信息。',
    outputsZh: ['与位置和时间相关的线索', '待复核的人员信息'],
    implementationWaysZh: ['专业人员观察与联络', '已有正式定义和验证依据的探测能力'],
    limitationsZh: ['没有观察到人员不等于确认无人。', '本主题当前未提供经专项核查的探测产品例子。'],
    exampleIds: [],
    purposes: {
      'HRF-HZ-03': '组织呼叫、观察及其他经核验探测所得的人员线索，记录位置、时间与不确定性。',
    },
  },
  {
    key: 'ux03-assisted-transfer',
    nameZh: '辅助转运支持',
    definitionZh: '了解人员转移所需的协同与运输辅助，不把运输设备等同于完整救援方案。',
    outputsZh: ['搬运或转移相关的支持', '协同与交接所需的信息'],
    implementationWaysZh: ['专业救援人员与团队', '适用的运输辅助器材或对应设备配置'],
    limitationsZh: [
      '路径、人员状况、团队操作和具体设备配置须确认。',
      '技术示例不提供医学处置或现场操作步骤。',
    ],
    exampleIds: ['EX-COLOSSUS-STRETCHER'],
    purposes: {
      'HRF-HZ-03': '了解人员转移可能需要的团队和运输辅助，区分发现人员与完成转移。',
      'HRF-HZ-04': '在通道条件已由专业人员确认的前提下，了解适用的转运辅助方式。',
    },
  },
  {
    key: 'ux03-building-information',
    nameZh: '建筑资料与专业核查',
    definitionZh: '把建筑资料、设施记录和观察线索对应起来，为专业核查提供资料基础。',
    outputsZh: ['建筑与通道关系说明', '需要补充或复核的资料清单'],
    implementationWaysZh: ['建筑与设施资料', '专业人员现场核查'],
    limitationsZh: [
      '资料不能证明本次现场状态；不得自动判定安全。',
      '资料与观察线索需要专业人员核对。',
    ],
    exampleIds: [],
    purposes: {
      'HRF-HZ-05': '把建筑图纸、防火分隔和现场观察对应起来，识别资料与实际情况需要核对的部分。',
      'HRF-HZ-04': '利用建筑与疏散资料理解通道关系，再由任务工作台承接本次通行事实。',
      'HRF-HZ-06': '把建筑资料与外围观察对应起来，标明需要专业核查的部位，不只依赖外观判断。',
    },
  },
] as const;

/** How a binding was dispositioned. Only these three appear. */
export type BindingReview = 'KEEP' | 'PRESENTATION_FIX' | 'NEEDS_SOURCE';

export interface CapabilityBinding {
  readonly issueCode: string;
  readonly capabilityCode: string;
  readonly topicKey: CapabilityTopicKey | null;
  readonly review: BindingReview;
  /** Why this capability and this topic belong together — or why none does. */
  readonly rationale: string;
  /**
   * Where the chosen topic covers only part of the source capability, this says which part.
   *
   * It exists because an independent review found two rows where the topic explains the
   * capability's INPUTS or its ADMINISTRATION but not its whole meaning: a "search and rescue …
   * locating and extracting occupants" capability was being illustrated by "organising person
   * clues", and a "structural condition assessment" capability by "building records and
   * professional verification". The topic is still the closest reviewed one, so the binding
   * stands — but the reader is told what it does not cover, and the gap is recorded as a Finding
   * rather than absorbed into a `PRESENTATION_FIX`.
   */
  readonly scopeNote?: string;
}

/**
 * The reviewed binding table, one row per (issue, source capability) pair that exists in
 * `issueRelations`. A row with `topicKey: null` and `NEEDS_SOURCE` is the honest answer for a
 * capability no reviewed topic explains; it is not a gap to paper over with another topic.
 */
export const capabilityBindings: readonly CapabilityBinding[] = [
  {
    issueCode: 'HRF-HZ-02',
    capabilityCode: 'HRF-CR-05',
    topicKey: 'ux03-ventilation',
    review: 'PRESENTATION_FIX',
    rationale:
      '源能力是“烟气管理与通风”。修正前抽屉按问题的设备组显示 M30T、Elios 3 与通用 Colossus；三者都不是通风器械。现只绑定通风主题，例子限于便携风机与 Colossus 大流量风机配置。',
  },
  {
    issueCode: 'HRF-HZ-02',
    capabilityCode: 'HRF-CR-13',
    topicKey: 'ux03-aerial-observation',
    review: 'PRESENTATION_FIX',
    rationale: '源能力是“高处远距与热成像观察”，与观察主题一致；例子限定为带热像相机的 M30T。',
  },
  {
    issueCode: 'HRF-HZ-02',
    capabilityCode: 'HRF-CR-15',
    topicKey: 'ux03-indoor-mapping',
    review: 'PRESENTATION_FIX',
    rationale: '源能力是“机器空间检查”，属于室内空间获取；例子限定为 Elios 3 室内检查配置。',
  },
  {
    issueCode: 'HRF-HZ-02',
    capabilityCode: 'HRF-CR-12',
    topicKey: 'ux03-communications',
    review: 'PRESENTATION_FIX',
    rationale: '源能力是“通信与楼内中继”，与通信主题一致。',
  },
  {
    issueCode: 'HRF-HZ-03',
    capabilityCode: 'HRF-CR-04',
    topicKey: 'ux03-person-search',
    review: 'PRESENTATION_FIX',
    rationale:
      '源能力是“烟气环境生命搜索”。主题如实声明当前没有经专项核查的探测产品例子，因此不挂任何设备。',
    scopeNote:
      '本主题只解释人员线索的组织与记录，不解释定位与救出的技术手段；该项内容尚无经核验的资料。',
  },
  {
    issueCode: 'HRF-HZ-03',
    capabilityCode: 'HRF-CR-06',
    topicKey: 'ux03-assisted-transfer',
    review: 'PRESENTATION_FIX',
    rationale: '源能力是“疏散支持”，与辅助转运主题一致；例子限定为担架支架配置且不附图片。',
  },
  {
    issueCode: 'HRF-HZ-03',
    capabilityCode: 'HRF-CR-12',
    topicKey: 'ux03-communications',
    review: 'PRESENTATION_FIX',
    rationale: '同一通信能力服务人员问题，但本题用途改为人员线索与协助需求的传递。',
  },
  {
    issueCode: 'HRF-HZ-04',
    capabilityCode: 'HRF-CR-01',
    topicKey: null,
    review: 'NEEDS_SOURCE',
    rationale:
      '源能力是“高处进入与撤离”。现有主题均不解释该项，且没有经核验的技术例子；保留原始说明并标注待完善，不借用其他主题。',
  },
  {
    issueCode: 'HRF-HZ-04',
    capabilityCode: 'HRF-CR-06',
    topicKey: 'ux03-assisted-transfer',
    review: 'PRESENTATION_FIX',
    rationale: '通道问题下的疏散支持，用途限定为“通道条件已确认后的转运辅助”。',
  },
  {
    issueCode: 'HRF-HZ-04',
    capabilityCode: 'HRF-CR-15',
    topicKey: 'ux03-indoor-mapping',
    review: 'PRESENTATION_FIX',
    rationale: '同一室内空间检查能力，本题用途改为整理通道布局与可见障碍。',
  },
  {
    issueCode: 'HRF-HZ-04',
    capabilityCode: 'HRF-CR-12',
    topicKey: 'ux03-communications',
    review: 'PRESENTATION_FIX',
    rationale: '同一通信能力，本题用途改为传递路径与障碍位置。',
  },
  {
    issueCode: 'HRF-HZ-01',
    capabilityCode: 'HRF-CR-02',
    topicKey: null,
    review: 'NEEDS_SOURCE',
    rationale:
      '源能力是“室内火势抑制”。远程灭火主题只解释远程实现的物理支持，不能等同室内抑制能力，故不绑定。',
  },
  {
    issueCode: 'HRF-HZ-01',
    capabilityCode: 'HRF-CR-13',
    topicKey: 'ux03-aerial-observation',
    review: 'PRESENTATION_FIX',
    rationale: '同一观察能力，本题用途改为火情分布与变化的外部观察。',
  },
  {
    issueCode: 'HRF-HZ-01',
    capabilityCode: 'HRF-CR-17',
    topicKey: 'ux03-remote-suppression',
    review: 'PRESENTATION_FIX',
    rationale: '源能力是“机器灭火支持”，与远程灭火主题一致；例子限定为水炮组合配置。',
  },
  {
    issueCode: 'HRF-HZ-01',
    capabilityCode: 'HRF-CR-12',
    topicKey: 'ux03-communications',
    review: 'PRESENTATION_FIX',
    rationale: '同一通信能力，本题用途改为回传观察图像与变化信息。',
  },
  {
    issueCode: 'HRF-HZ-05',
    capabilityCode: 'HRF-CR-08',
    topicKey: 'ux03-building-information',
    review: 'PRESENTATION_FIX',
    rationale:
      '源能力是“结构状态评估”，其依据是建筑资料与专业核查。位移监测主题属监测手段而非评估结论，故不绑定，避免把监测读数说成安全鉴定。',
    scopeNote:
      '本主题解释评估所依据的建筑资料与专业核查，不解释评估结论本身；结论仍由具备资质的专业人员作出。',
  },
  {
    issueCode: 'HRF-HZ-05',
    capabilityCode: 'HRF-CR-13',
    topicKey: 'ux03-aerial-observation',
    review: 'PRESENTATION_FIX',
    rationale: '同一观察能力，本题用途改为危险高处的外围损伤线索观察，明确不替代结构评估。',
  },
  {
    issueCode: 'HRF-HZ-05',
    capabilityCode: 'HRF-CR-12',
    topicKey: 'ux03-communications',
    review: 'PRESENTATION_FIX',
    rationale: '同一通信能力，本题用途改为损伤观察与位置记录的回传。',
  },
  {
    issueCode: 'HRF-HZ-06',
    capabilityCode: 'HRF-CR-08',
    topicKey: 'ux03-building-information',
    review: 'PRESENTATION_FIX',
    rationale: '外围构件异常需要依据资料与专业核查，不绑定位移监测以免暗示可自动判定稳定。',
    scopeNote:
      '本主题解释评估所依据的建筑资料与专业核查，不解释稳定性结论本身；构件是否稳定仍需专业人员判定。',
  },
  {
    issueCode: 'HRF-HZ-06',
    capabilityCode: 'HRF-CR-13',
    topicKey: 'ux03-aerial-observation',
    review: 'PRESENTATION_FIX',
    rationale: '同一观察能力，本题用途改为立面与高处构件的可见异常观察。',
  },
  {
    issueCode: 'HRF-HZ-06',
    capabilityCode: 'HRF-CR-19',
    topicKey: null,
    review: 'NEEDS_SOURCE',
    rationale: '源能力是“外围隔离与交通管理”，现有主题均不解释该项，保留原始说明并标注待完善。',
  },
] as const;

export function findTopic(key: CapabilityTopicKey): CapabilityTopic | undefined {
  return capabilityTopics.find((topic) => topic.key === key);
}

export function findBinding(
  issueCode: string,
  capabilityCode: string,
): CapabilityBinding | undefined {
  return capabilityBindings.find(
    (row) => row.issueCode === issueCode && row.capabilityCode === capabilityCode,
  );
}

/* ------------------------------------------------------------------ technical examples */

export type VideoKind =
  | 'official_video_catalog'
  | 'official_demo_page'
  | 'official_linked_video'
  | 'official_page_with_video';

export interface MediaExample {
  readonly id: string;
  readonly nameZh: string;
  readonly manufacturerModel: string;
  /** Which module or camera configuration this example depicts. Never "the robot" in general. */
  readonly configuration: string;
  readonly topicKeys: readonly CapabilityTopicKey[];
  readonly officialPageUrl: string;
  readonly pageLinkLabel: string;
  readonly videoUrl: string | null;
  readonly videoKind: VideoKind | null;
  readonly videoLinkLabel: string | null;
  /** What to look at. Never a fabricated timestamp or duration. */
  readonly watchForZh: string;
  readonly limitationZh: string;
  /** The kit's asset id, kept so media-verification.json can point at the same record. */
  readonly imageRef: string | null;
  readonly rightsStatus: 'unconfirmed';
}

export const mediaExamples: readonly MediaExample[] = [
  {
    id: 'EX-DJI-M30T',
    nameZh: '高处观察无人机｜M30T',
    manufacturerModel: 'DJI M30T',
    configuration: '带热成像相机的 M30T 配置（不笼统以 M30 替代）',
    topicKeys: ['ux03-aerial-observation'],
    officialPageUrl: 'https://www.dji.com/support/product/matrice-30',
    pageLinkLabel: '查看官方图文',
    videoUrl: 'https://enterprise.dji.com/matrice-30/video',
    videoKind: 'official_video_catalog',
    videoLinkLabel: '查看官方演示',
    watchForZh: '看相机视图如何呈现外部可见光和热像信息。',
    limitationZh: '仅作为观察实现方式的一例，不提供气体成分或穿墙判断。',
    imageRef: 'IMG-DJI-POSTER',
    rightsStatus: 'unconfirmed',
  },
  {
    id: 'EX-ELIOS-3',
    nameZh: '室内检查无人机｜Elios 3',
    manufacturerModel: 'Flyability Elios 3',
    configuration: '室内工业检查，含相应 LiDAR 与报告功能',
    topicKeys: ['ux03-indoor-mapping'],
    officialPageUrl: 'https://www.flyability.com/elios-3',
    pageLinkLabel: '查看官方图文',
    videoUrl: 'https://www.flyability.com/typ-demo-video-elios-3',
    videoKind: 'official_demo_page',
    videoLinkLabel: '查看官方演示',
    watchForZh: '看检查图像如何与三维空间结果关联。',
    limitationZh: '工业检查演示不等于高温火场认证，也不表示任何现场均可进入。',
    imageRef: 'IMG-ELIOS',
    rightsStatus: 'unconfirmed',
  },
  {
    id: 'EX-BATFAN',
    nameZh: '便携式通风设备｜BATFAN 3 Li+',
    manufacturerModel: 'LEADER BATFAN 3 Li+',
    configuration: '风机本体，及按厂家说明配套的管道配置',
    topicKeys: ['ux03-ventilation'],
    officialPageUrl:
      'https://www.leader-group.company/en/firefighting-equipment/fire-ventilators/battery-fans/firefighting-battery-fan-swappable-lithium-battery-batfan-3-li',
    pageLinkLabel: '查看官方图文',
    videoUrl: 'https://www.youtube.com/watch?v=l91G729Fo2c',
    videoKind: 'official_linked_video',
    videoLinkLabel: '查看官方演示',
    watchForZh: '看设备本体与通风相关配套形态；不把演示当作现场部署方案。',
    limitationZh: '不据宣传片确定现场风机位置、方向、开机时机或参数。',
    imageRef: 'IMG-BATFAN-THUMB',
    rightsStatus: 'unconfirmed',
  },
  {
    id: 'EX-COLOSSUS-FAN',
    nameZh: '机器人通风配置｜Colossus',
    manufacturerModel: 'Shark Robotics Colossus',
    configuration: 'Large flow fan（大流量风机）模块',
    topicKeys: ['ux03-ventilation'],
    officialPageUrl: 'https://www.shark-robotics.com/colossus-firefighting-robot/',
    pageLinkLabel: '查看官方图文',
    videoUrl: null,
    videoKind: null,
    videoLinkLabel: null,
    watchForZh: '看履带底盘与大流量风机的组合，识别模块差异。',
    limitationZh: '不能用同型号的水炮照片说明通风；具体配置与现场适用性仍需核验。',
    imageRef: 'IMG-COLOSSUS-FAN',
    rightsStatus: 'unconfirmed',
  },
  {
    id: 'EX-COLOSSUS-WATER',
    nameZh: '远程灭火配置｜Colossus',
    manufacturerModel: 'Shark Robotics Colossus',
    configuration: '消防水炮组合配置',
    topicKeys: ['ux03-remote-suppression'],
    officialPageUrl: 'https://www.shark-robotics.com/colossus-firefighting-robot/',
    pageLinkLabel: '查看官方图文',
    videoUrl: null,
    videoKind: null,
    videoLinkLabel: null,
    watchForZh: '看遥控底盘与水炮的组合，理解远程物理支持的形态。',
    limitationZh: '产品介绍不说明本地已有设备、供水、运输条件或操作授权。',
    imageRef: 'IMG-COLOSSUS-WATER',
    rightsStatus: 'unconfirmed',
  },
  {
    id: 'EX-COLOSSUS-STRETCHER',
    nameZh: '辅助转运配置｜Colossus',
    manufacturerModel: 'Shark Robotics Colossus',
    configuration: 'Stretcher support（担架支架）模块',
    topicKeys: ['ux03-assisted-transfer'],
    officialPageUrl: 'https://www.shark-robotics.com/colossus-firefighting-robot/',
    pageLinkLabel: '查看官方图文',
    videoUrl: null,
    videoKind: null,
    videoLinkLabel: null,
    watchForZh: '查看官网担架支架配置；不要借用水炮或风机图片。',
    limitationZh: '仅为特定配置的转运技术例，不等于完成全部人员搜救。',
    imageRef: null,
    rightsStatus: 'unconfirmed',
  },
  {
    id: 'EX-HYTERA',
    nameZh: '应急自组网方案｜海能达',
    manufacturerModel: 'Hytera 应急自组网解决方案',
    configuration: '方案级介绍；具体设备与频段需另查',
    topicKeys: ['ux03-communications'],
    officialPageUrl:
      'https://www.hytera.com/cn/product-new/emergency-response/fast-deployment-products-solutions.html',
    pageLinkLabel: '查看官方图文',
    videoUrl:
      'https://www.hytera.com/cn/product-new/emergency-response/fast-deployment-products-solutions.html',
    videoKind: 'official_page_with_video',
    videoLinkLabel: '查看官方演示',
    watchForZh: '看现场终端、临时通信节点与指挥端如何连接。',
    limitationZh: '视频示意不证明任意建筑都有覆盖，也不证明已接入本平台。',
    imageRef: 'IMG-HYTERA-POSTER',
    rightsStatus: 'unconfirmed',
  },
  {
    id: 'EX-XAM8000',
    nameZh: '便携气体检测仪｜X-am 8000',
    manufacturerModel: 'Dräger X-am 8000',
    configuration: '检测对象取决于实际传感器与配置',
    topicKeys: ['ux03-gas-detection'],
    officialPageUrl: 'https://www.draeger.com/zh_cn/Products/x-am-8000',
    pageLinkLabel: '查看官方图文',
    videoUrl: null,
    videoKind: null,
    videoLinkLabel: null,
    watchForZh: '看仪器的测量与报警界面、以及传感器配置说明。',
    limitationZh: '不把厂商展示屏读数当作当前场所测值；不保证所有危险气体都可检测。',
    imageRef: 'IMG-DRAEGER',
    rightsStatus: 'unconfirmed',
  },
  {
    id: 'EX-SENTRY',
    nameZh: '结构位移监测设备｜SENTRY B2',
    manufacturerModel: 'LEADER SENTRY B2',
    configuration: '结构位移监测；具体设备配置需核对',
    topicKeys: ['ux03-movement-monitoring'],
    officialPageUrl:
      'https://www.leader-group.company/en/safety-and-monitoring-equipment/structural-movement-monitor-telemeter-stability-controller/structural-movement-telemeter-leader-sentry-b2',
    pageLinkLabel: '查看官方图文',
    videoUrl: 'https://www.youtube.com/watch?v=aj4aNFHKu_E',
    videoKind: 'official_linked_video',
    videoLinkLabel: '查看官方演示',
    watchForZh: '看测量目标、变化显示和告警关系。',
    limitationZh: '测量目标的位移记录不等于整栋建筑已经安全。',
    imageRef: 'IMG-SENTRY-THUMB',
    rightsStatus: 'unconfirmed',
  },
] as const;

/**
 * The examples a topic may name in the drawer.
 *
 * The filter is exact on BOTH sides: the example must be listed by the topic AND must itself
 * declare that topic. An example can never appear because it happens to be in the scenario,
 * because it shares a chassis with another module, or because it is first in a list.
 */
export function examplesForTopic(topicKey: CapabilityTopicKey): readonly MediaExample[] {
  return mediaExamples.filter(
    (example) =>
      example.topicKeys.includes(topicKey) &&
      findTopic(topicKey)?.exampleIds.includes(example.id) === true,
  );
}

/* ------------------------------------------------------------------ official sources */

export interface ExplainerSource {
  readonly id: string;
  readonly title: string;
  readonly publisher: string;
  readonly url: string;
  readonly section: string;
  /** What this page actually supports. */
  readonly supportedSummary: string;
  readonly kind: '厂商产品页' | '厂商资料页' | '官方解释' | '交互规范参考';
}

export const explainerSources: readonly ExplainerSource[] = [
  {
    id: 'SRC-DJI-SUPPORT',
    title: 'Matrice 30 系列官方支持',
    publisher: 'DJI',
    url: 'https://www.dji.com/support/product/matrice-30',
    section: '常见问题 / 飞行器 / M30 与 M30T 的差异',
    supportedSummary: 'M30 与 M30T 的相机配置不同；热成像示例需明确使用 M30T。',
    kind: '厂商产品页',
  },
  {
    id: 'SRC-DJI-VIDEO',
    title: 'Matrice 30 系列视频目录',
    publisher: 'DJI',
    url: 'https://enterprise.dji.com/matrice-30/video',
    section: '相机视图等视频条目',
    supportedSummary: '官方目录提供相机视图等视频条目；本轮未逐段播放。',
    kind: '厂商产品页',
  },
  {
    id: 'SRC-ELIOS',
    title: 'Elios 3 产品页',
    publisher: 'Flyability',
    url: 'https://www.flyability.com/elios-3',
    section: '三维实时地图 / 三维报告',
    supportedSummary: '官方描述室内检查、LiDAR 空间获取与三维结果呈现；不是本平台的火场适用验证。',
    kind: '厂商产品页',
  },
  {
    id: 'SRC-ELIOS-DEMO',
    title: 'Elios 3 官方演示页',
    publisher: 'Flyability',
    url: 'https://www.flyability.com/typ-demo-video-elios-3',
    section: '演示视频',
    supportedSummary: '页面含官方演示介绍；本轮未测播放器与目标网络可播放性。',
    kind: '厂商产品页',
  },
  {
    id: 'SRC-COLOSSUS',
    title: 'Colossus 模块化消防机器人',
    publisher: 'Shark Robotics',
    url: 'https://www.shark-robotics.com/colossus-firefighting-robot/',
    section: '模块化配置',
    supportedSummary: '官网分别展示水炮、担架支架与大流量风机配置；使用时应区分模块。',
    kind: '厂商产品页',
  },
  {
    id: 'SRC-BATFAN',
    title: 'BATFAN 3 Li+ 电池风机',
    publisher: 'LEADER',
    url: 'https://www.leader-group.company/en/firefighting-equipment/fire-ventilators/battery-fans/firefighting-battery-fan-swappable-lithium-battery-batfan-3-li',
    section: '产品特点与视频入口',
    supportedSummary: '官方页面描述通风及相应管道配置，并链接产品演示。',
    kind: '厂商产品页',
  },
  {
    id: 'SRC-SENTRY',
    title: 'SENTRY B2 结构位移监测',
    publisher: 'LEADER',
    url: 'https://www.leader-group.company/en/safety-and-monitoring-equipment/structural-movement-monitor-telemeter-stability-controller/structural-movement-telemeter-leader-sentry-b2',
    section: '产品特点与视频入口',
    supportedSummary: '官方介绍激光位移监测、变化显示与告警。',
    kind: '厂商产品页',
  },
  {
    id: 'SRC-HYTERA',
    title: '应急通信解决方案',
    publisher: '海能达',
    url: 'https://www.hytera.com/cn/product-new/emergency-response/fast-deployment-products-solutions.html',
    section: '概述 / 应急自组网解决方案视频',
    supportedSummary: '中文页面介绍临时组网方案并标注视频入口；不是对实际网络的测试。',
    kind: '厂商产品页',
  },
  {
    id: 'SRC-DRAEGER-CN',
    title: 'X-am 8000 中文产品页',
    publisher: '德尔格',
    url: 'https://www.draeger.com/zh_cn/Products/x-am-8000',
    section: '产品概述 / 传感器及配件',
    supportedSummary: '介绍多种气体检测与不同传感器组合；具体检测对象依配置。',
    kind: '厂商产品页',
  },
  {
    id: 'SRC-DRAEGER-EN',
    title: 'X-am 8000 官方英文资料页',
    publisher: 'Dräger',
    url: 'https://www.draeger.com/en-us_us/Products/X-am-8000',
    section: '产品亮点 / 下载资料',
    supportedSummary: '官方产品与资料入口；本轮不虚构可播放的视频直链。',
    kind: '厂商资料页',
  },
  {
    id: 'SRC-FLIR',
    title: '热成像能否穿墙：官方解释',
    publisher: 'FLIR',
    url: 'https://www.flir.com/en-gb/discover/home-outdoor/can-thermal-imaging-see-through-walls/',
    section: 'Can thermal imaging see through walls',
    supportedSummary: '用于说明表面热信息与穿墙观察的区别。',
    kind: '官方解释',
  },
  {
    id: 'SRC-W3C',
    title: '模态对话框交互模式',
    publisher: 'W3C WAI',
    url: 'https://www.w3.org/WAI/ARIA/apg/patterns/dialog-modal/',
    section: '键盘交互',
    supportedSummary: '用于模态焦点、Tab 循环、Escape 与焦点恢复；仅工程参考。',
    kind: '交互规范参考',
  },
] as const;

export function findExplainerSource(id: string): ExplainerSource | undefined {
  return explainerSources.find((row) => row.id === id);
}

/** The sources a topic rests on, for the drawer's 官方依据 block. */
export function sourcesForTopic(topicKey: CapabilityTopicKey): readonly ExplainerSource[] {
  const byExample = examplesForTopic(topicKey).flatMap((example) =>
    explainerSources.filter((source) => exampleSourceIds(example.id).includes(source.id)),
  );
  const extra = capabilityTopicSourceIds[topicKey] ?? [];
  const rows = [...byExample, ...explainerSources.filter((source) => extra.includes(source.id))];
  return rows.filter((row, index) => rows.findIndex((other) => other.id === row.id) === index);
}

/** The registry rows each example cites. Kept beside the examples so the two cannot drift. */
export function exampleSourceIds(exampleId: string): readonly string[] {
  return exampleSourceIdsByExample[exampleId] ?? [];
}

const exampleSourceIdsByExample: Readonly<Record<string, readonly string[]>> = {
  'EX-DJI-M30T': ['SRC-DJI-SUPPORT', 'SRC-DJI-VIDEO', 'SRC-FLIR'],
  'EX-ELIOS-3': ['SRC-ELIOS', 'SRC-ELIOS-DEMO'],
  'EX-BATFAN': ['SRC-BATFAN'],
  'EX-COLOSSUS-FAN': ['SRC-COLOSSUS'],
  'EX-COLOSSUS-WATER': ['SRC-COLOSSUS'],
  'EX-COLOSSUS-STRETCHER': ['SRC-COLOSSUS'],
  'EX-HYTERA': ['SRC-HYTERA'],
  'EX-XAM8000': ['SRC-DRAEGER-CN', 'SRC-DRAEGER-EN'],
  'EX-SENTRY': ['SRC-SENTRY'],
};

const capabilityTopicSourceIds: Readonly<Record<CapabilityTopicKey, readonly string[]>> = {
  'ux04-forest-monitoring': [],
  'ux03-aerial-observation': ['SRC-FLIR'],
  'ux03-indoor-mapping': [],
  'ux03-ventilation': [],
  'ux03-communications': [],
  'ux03-gas-detection': [],
  'ux03-movement-monitoring': [],
  'ux03-remote-suppression': [],
  'ux03-person-search': [],
  'ux03-assisted-transfer': [],
  'ux03-building-information': [],
};

/** SC-UX03-F02: two semantic gaps, three affected pairs; content Owner has not approved a fix. */
export function bindingPendingOwner(issueCode: string, capabilityCode: string): boolean {
  return (
    (issueCode === 'HRF-HZ-03' && capabilityCode === 'HRF-CR-04') ||
    ((issueCode === 'HRF-HZ-05' || issueCode === 'HRF-HZ-06') && capabilityCode === 'HRF-CR-08')
  );
}
